/**
 * `/admin/signups` view — admin-managed signup email allowlist.
 *
 * Architecture mirrors `admin/llm-view.ts`:
 *   - the view function produces the static landmark + heading skeleton +
 *     anchor nodes; a single `onMount` hook fetches the state and
 *     populates the panels imperatively. Route-local data (re-fetched per
 *     navigate), kept out of the global Model.
 *   - 401/404 → a single authoritative not-found / sign-in overlay (the
 *     worker `requireSystemAdmin` returns 404 to non-admins).
 *
 * Accessibility: `role="region"` panels with `aria-labelledby`, a
 * `role="status" aria-live="polite"` status node, `role="alert"` errors,
 * labelled inputs, icon-only buttons carry `aria-label`.
 */
import { button, pageHeader, sectionHeading, textInput } from '@dl/ui';
import {
  div,
  form,
  input,
  label,
  main,
  p,
  section,
  span,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

import type { AdminResult } from './admin-fetch.js';
import { maybeRenderOverlay as renderOverlay } from './admin-overlay.js';
import { onAdminMount } from './mount.js';
import {
  addAllowlistEmail,
  getSignups,
  removeAllowlistEmail,
  setSignupMode,
  type AllowlistEntry,
  type SignupsState,
} from './signups-api.js';

function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

function formatAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function maybeRenderOverlay(root: Element, result: AdminResult<unknown>): boolean {
  return renderOverlay(root, result, {
    testid: 'admin-signups-overlay',
    notFoundHeading: t('admin.signups.not_found.heading'),
    notFoundBody: t('admin.signups.not_found.body'),
    unauthHeading: t('admin.signups.unauthenticated.heading'),
    unauthBody: t('admin.signups.unauthenticated.body'),
    unauthLink: t('admin.signups.unauthenticated.link'),
    signInTestid: 'admin-signups-sign-in-link',
    hideSelector: '.admin-panel',
    introSelector: '.dl-page-header',
  });
}

// ───────────────────────────────────────────────────────── state cache ─

interface SignupsCache {
  enabled: boolean;
  allowlist: AllowlistEntry[];
}

const cache: SignupsCache = { enabled: false, allowlist: [] };

function renderAllowlist(listEl: Element, statusEl: Element): void {
  clear(listEl);
  if (cache.allowlist.length === 0) {
    statusEl.textContent = t('admin.signups.allowlist.empty');
    return;
  }
  statusEl.textContent = '';
  const ul = document.createElement('ul');
  ul.setAttribute('class', 'admin-signups-allowlist');
  ul.setAttribute('role', 'list');
  for (const entry of cache.allowlist) {
    const li = document.createElement('li');
    li.setAttribute('class', 'admin-signups-row');
    li.setAttribute('data-testid', `admin-signups-row-${entry.email}`);

    const emailEl = document.createElement('span');
    emailEl.setAttribute('class', 'admin-signups-email');
    emailEl.textContent = entry.email;
    li.appendChild(emailEl);

    if (entry.note) {
      const noteEl = document.createElement('span');
      noteEl.setAttribute('class', 'admin-signups-note');
      noteEl.textContent = entry.note;
      li.appendChild(noteEl);
    }

    const atEl = document.createElement('span');
    atEl.setAttribute('class', 'admin-signups-at');
    atEl.textContent = formatAt(entry.added_at);
    li.appendChild(atEl);

    const removeBtn = document.createElement('button');
    removeBtn.setAttribute('type', 'button');
    removeBtn.setAttribute('class', 'admin-signups-remove');
    removeBtn.setAttribute('data-testid', `admin-signups-remove-${entry.email}`);
    removeBtn.setAttribute('aria-label', t('admin.signups.allowlist.remove_label', { email: entry.email }));
    removeBtn.textContent = t('admin.signups.allowlist.remove');
    removeBtn.addEventListener('click', () => {
      void (async (): Promise<void> => {
        removeBtn.disabled = true;
        const res = await removeAllowlistEmail(entry.email);
        removeBtn.disabled = false;
        if (!res.ok) {
          statusEl.textContent = t('admin.signups.error.generic');
          return;
        }
        cache.allowlist = cache.allowlist.filter((e) => e.email !== entry.email);
        renderAllowlist(listEl, statusEl);
      })();
    });
    li.appendChild(removeBtn);

    ul.appendChild(li);
  }
  listEl.appendChild(ul);
}

function syncToggle(toggleEl: HTMLInputElement): void {
  toggleEl.checked = cache.enabled;
  toggleEl.setAttribute(
    'aria-label',
    cache.enabled ? t('admin.signups.mode.toggle_off') : t('admin.signups.mode.toggle_on'),
  );
}

// ──────────────────────────────────────────────────────────────── view ─

export function viewAdminSignups<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
): Mountable {
  cache.enabled = false;
  cache.allowlist = [];

  const mountHook = onAdminMount('[data-admin-signups-root]', (root) => {
    let cancelled = false;

    void (async (): Promise<void> => {
      const res = await getSignups();
      if (cancelled) return;
      if (maybeRenderOverlay(root, res)) return;
      if (!res.ok) {
        const statusEl = root.querySelector('[data-signups-status]');
        if (statusEl) statusEl.textContent = t('admin.signups.error.generic');
        return;
      }
      const state: SignupsState = res.value;
      cache.enabled = state.enabled;
      cache.allowlist = state.allowlist;

      const toggleEl = root.querySelector<HTMLInputElement>('[data-signups-toggle]');
      if (toggleEl) {
        syncToggle(toggleEl);
        toggleEl.addEventListener('change', () => {
          void (async (): Promise<void> => {
            toggleEl.disabled = true;
            const r = await setSignupMode(toggleEl.checked);
            toggleEl.disabled = false;
            if (!r.ok) {
              syncToggle(toggleEl); // revert
              const s = root.querySelector('[data-signups-status]');
              if (s) s.textContent = t('admin.signups.error.generic');
              return;
            }
            cache.enabled = toggleEl.checked;
            syncToggle(toggleEl);
          })();
        });
      }

      const listEl = root.querySelector('[data-allowlist-list]');
      const allowStatusEl = root.querySelector('[data-allowlist-status]');
      if (listEl && allowStatusEl) renderAllowlist(listEl, allowStatusEl);

      // Add-email form.
      const formEl = root.querySelector<HTMLFormElement>('[data-signups-add-form]');
      const errEl = root.querySelector('[data-signups-add-error]');
      if (formEl) {
        formEl.addEventListener('submit', (ev) => {
          ev.preventDefault();
          void (async (): Promise<void> => {
            if (errEl) errEl.textContent = '';
            const data = new FormData(formEl);
            const email = String(data.get('email') ?? '').trim();
            const note = String(data.get('note') ?? '').trim();
            if (email.length === 0) {
              if (errEl) errEl.textContent = t('admin.signups.allowlist.email_required');
              return;
            }
            const submit = formEl.querySelector<HTMLButtonElement>('button[type="submit"]');
            if (submit) submit.disabled = true;
            const r = await addAllowlistEmail(email, note);
            if (submit) submit.disabled = false;
            if (!r.ok) {
              if (errEl) {
                errEl.textContent =
                  r.kind === 'error' && r.status === 409
                    ? t('admin.signups.allowlist.duplicate')
                    : t('admin.signups.error.generic');
              }
              return;
            }
            // Prepend the new entry (server orders newest-first).
            cache.allowlist = [r.value, ...cache.allowlist];
            if (listEl && allowStatusEl) renderAllowlist(listEl, allowStatusEl);
            formEl.reset();
          })();
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  });

  return main(
    {
      role: 'main',
      'aria-labelledby': 'admin-signups-heading',
      'data-admin-signups-root': '',
      'data-testid': 'admin-signups-view',
    },
    [
      mountHook,
      pageHeader({
        title: text(t('admin.signups.title')),
        titleId: 'admin-signups-heading',
        intro: text(t('admin.signups.intro')),
      }),
      div(
        {
          'data-signups-status': '',
          role: 'status',
          'aria-live': 'polite',
          class: 'admin-panel-status',
        },
        [],
      ),

      // Mode panel ──────────────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-signups-mode',
          'aria-labelledby': 'admin-signups-mode-heading',
          'data-testid': 'admin-signups-mode-panel',
        },
        [
          sectionHeading({ title: text(t('admin.signups.mode.title')), level: 2, id: 'admin-signups-mode-heading' }),
          p({ class: 'admin-signups-mode-help' }, [text(t('admin.signups.mode.help'))]),
          label({ for: 'admin-signups-toggle', class: 'admin-signups-toggle-row' }, [
            input({
              id: 'admin-signups-toggle',
              type: 'checkbox',
              'data-signups-toggle': '',
              'data-testid': 'admin-signups-toggle',
            }),
            span([text(t('admin.signups.mode.label'))]),
          ]),
        ],
      ),

      // Allowlist panel ─────────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-signups-allowlist',
          'aria-labelledby': 'admin-signups-allowlist-heading',
          'data-testid': 'admin-signups-allowlist-panel',
        },
        [
          sectionHeading({ title: text(t('admin.signups.allowlist.title')), level: 2, id: 'admin-signups-allowlist-heading' }),
          form(
            {
              class: 'admin-signups-add-form',
              'data-signups-add-form': '',
              novalidate: true,
            },
            [
              div({ class: 'admin-signups-add-row' }, [
                label({ for: 'admin-signups-add-email' }, [
                  text(t('admin.signups.allowlist.email_label')),
                ]),
                textInput({
                  type: 'email',
                  testid: 'admin-signups-add-email',
                  attrs: {
                    id: 'admin-signups-add-email',
                    name: 'email',
                    autocomplete: 'off',
                    'aria-describedby': 'admin-signups-add-error',
                  },
                }),
              ]),
              div({ class: 'admin-signups-add-row' }, [
                label({ for: 'admin-signups-add-note' }, [
                  text(t('admin.signups.allowlist.note_label')),
                ]),
                textInput({
                  type: 'text',
                  testid: 'admin-signups-add-note',
                  attrs: {
                    id: 'admin-signups-add-note',
                    name: 'note',
                    autocomplete: 'off',
                  },
                }),
              ]),
              span(
                {
                  id: 'admin-signups-add-error',
                  'data-signups-add-error': '',
                  class: 'auth-field-error',
                  role: 'alert',
                },
                [],
              ),
              button({
                variant: 'primary',
                type: 'submit',
                testid: 'admin-signups-add-submit',
                children: [text(t('admin.signups.allowlist.add'))],
              }),
            ],
          ),
          div(
            {
              'data-allowlist-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div({ 'data-allowlist-list': '', 'data-testid': 'admin-signups-allowlist' }, []),
        ],
      ),
    ],
  );
}
